import { ref, computed } from 'vue';
import { $http } from '../stores/http.js';

/**
 * Composable for managing "Select All by Filter" functionality.
 * Supports two selection modes:
 * - 'manual': user clicked specific items (ctrl+click, shift+click)
 * - 'filter': user clicked "Select All" for current filter
 *
 * @param {string} entityType - 'assets', 'templates', or 'channels'
 */
export function useFilterSelection(entityType) {
  // Selection mode: 'manual' or 'filter'
  const selectionMode = ref('manual');

  // UIDs selected manually (ctrl+click)
  const manualSelectedUids = ref([]);

  // UIDs selected by filter (all matching items)
  const filterSelectedUids = ref([]);

  // Total count of filter matches (may include non-visible items)
  const totalFilterCount = ref(0);

  // Currently visible UIDs (loaded in viewport)
  const visibleUids = ref([]);

  // Computed: active selected UIDs based on mode
  const selectedUids = computed(() =>
    selectionMode.value === 'filter'
      ? filterSelectedUids.value
      : manualSelectedUids.value
  );

  // Computed: count of selected items that are not currently visible
  const nonVisibleSelectedCount = computed(() => {
    if (selectionMode.value !== 'filter') return 0;
    const visibleSet = new Set(visibleUids.value);
    return filterSelectedUids.value.filter((uid) => !visibleSet.has(uid)).length;
  });

  // Computed: count of selected items that are visible
  const visibleSelectedCount = computed(() => {
    const visibleSet = new Set(visibleUids.value);
    return selectedUids.value.filter((uid) => visibleSet.has(uid)).length;
  });

  // Computed: whether any non-visible items are selected
  const hasNonVisibleSelected = computed(() => nonVisibleSelectedCount.value > 0);

  /**
   * Fetch all UIDs matching current filter and select them.
   * @param {Object} filterParams - Filter parameters to pass to the backend
   */
  async function selectAllByFilter(filterParams) {
    try {
      let endpoint = '';
      let queryParams = new URLSearchParams();

      if (entityType === 'assets') {
        endpoint = '/get_asset_uids_by_filter';
        if (filterParams.filter_tags) queryParams.set('filter_tags', filterParams.filter_tags);
        if (filterParams.search_query) queryParams.set('search_query', filterParams.search_query);
        if (filterParams.required_tags) queryParams.set('required_tags', filterParams.required_tags);
        if (filterParams.group_uid) queryParams.set('group_uid', filterParams.group_uid);
        if (filterParams.include_assets !== undefined) queryParams.set('include_assets', filterParams.include_assets);
        if (filterParams.include_groups !== undefined) queryParams.set('include_groups', filterParams.include_groups);
      } else if (entityType === 'templates') {
        endpoint = '/get_template_uids_by_filter';
        if (filterParams.filter_tags) queryParams.set('filter_tags', filterParams.filter_tags);
        if (filterParams.search_query) queryParams.set('search_query', filterParams.search_query);
        if (filterParams.preset) queryParams.set('preset', filterParams.preset);
        if (filterParams.src_file_filter) queryParams.set('src_file_filter', filterParams.src_file_filter);
      } else if (entityType === 'channels') {
        // Channels are filtered client-side, no backend call needed
        // filterParams.allChannelUids should be passed with pre-filtered UIDs
        filterSelectedUids.value = filterParams.allChannelUids || [];
        totalFilterCount.value = filterSelectedUids.value.length;
        selectionMode.value = 'filter';
        return;
      }

      const query = queryParams.toString();
      const url = query ? `${endpoint}?${query}` : endpoint;
      const response = JSON.parse(await $http('GET', url));

      filterSelectedUids.value = response.uids || [];
      totalFilterCount.value = response.total_count || 0;
      selectionMode.value = 'filter';
    } catch (error) {
      console.error(`Failed to fetch ${entityType} UIDs by filter:`, error);
    }
  }

  /**
   * Toggle manual selection of an item (ctrl+click behavior).
   * Switches to manual mode if in filter mode.
   * @param {string} uid - UID of item to toggle
   */
  function toggleManualSelection(uid) {
    // Switch to manual mode
    selectionMode.value = 'manual';

    const index = manualSelectedUids.value.indexOf(uid);
    if (index === -1) {
      manualSelectedUids.value.push(uid);
    } else {
      manualSelectedUids.value.splice(index, 1);
    }
  }

  /**
   * Set manual selection directly (for shift+click range selection).
   * @param {string[]} uids - Array of UIDs to select
   */
  function setManualSelection(uids) {
    selectionMode.value = 'manual';
    manualSelectedUids.value = [...uids];
  }

  /**
   * Add UIDs to manual selection (for shift+click).
   * @param {string[]} uids - Array of UIDs to add
   */
  function addToManualSelection(uids) {
    selectionMode.value = 'manual';
    for (const uid of uids) {
      if (!manualSelectedUids.value.includes(uid)) {
        manualSelectedUids.value.push(uid);
      }
    }
  }

  /**
   * Update visible UIDs (call when items load).
   * @param {string[]} uids - Array of currently visible UIDs
   */
  function updateVisibleUids(uids) {
    visibleUids.value = [...uids];
  }

  /**
   * Clear all selection state.
   */
  function clearSelection() {
    selectionMode.value = 'manual';
    manualSelectedUids.value = [];
    filterSelectedUids.value = [];
    totalFilterCount.value = 0;
  }

  /**
   * Check if a specific UID is selected.
   * @param {string} uid - UID to check
   * @returns {boolean}
   */
  function isSelected(uid) {
    return selectedUids.value.includes(uid);
  }

  return {
    // State
    selectionMode,
    selectedUids,
    totalFilterCount,
    visibleUids,
    nonVisibleSelectedCount,
    visibleSelectedCount,
    hasNonVisibleSelected,

    // Methods
    selectAllByFilter,
    toggleManualSelection,
    setManualSelection,
    addToManualSelection,
    updateVisibleUids,
    clearSelection,
    isSelected,
  };
}
